import { useEffect, useMemo, useRef, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import {
  getCohortId,
  getOrAssignVariant,
  subscribeToAssignment,
} from "@/lib/experimentStorage";
import { currentPagePath, serviceFromPath } from "@/lib/pageSegment";

/**
 * Lightweight A/B testing hook.
 * - Deterministically assigns a variant per visitor using localStorage with a
 *   30-day TTL, so users stay in the same cohort across tabs and refreshes.
 * - Logs a "view" event once per mount.
 * - Auto-attaches `page_path` and `service` to every event so admin views can
 *   segment conversion rate by where the user is in the site (e.g. dog vs
 *   deer fence pages).
 * - Returns the assigned variant + a `track` function for downstream events
 *   (click, convert, etc.).
 *
 * Events are written to the `experiment_events` table (public insert,
 * admin read).
 */

export function useExperiment(experimentKey: string, variants: string[]) {
  const sessionId = useMemo(() => getCohortId(), []);
  // SSR-safe: render the control variant on the server and during the first
  // client render, then adopt the visitor's assigned variant after hydration.
  // Assigning during render would read localStorage and cause a hydration
  // mismatch (server HTML vs client HTML differ).
  const [variant, setVariant] = useState<string>(() => variants[0] ?? "control");
  const [assigned, setAssigned] = useState(false);
  const viewedRef = useRef(false);

  useEffect(() => {
    const next = getOrAssignVariant(experimentKey, variants);
    if (next) setVariant((prev) => (next !== prev ? next : prev));
    setAssigned(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [experimentKey]);


  // Cross-tab sync: if another tab assigned (or rolled over) the variant
  // first, adopt that value here so cohorts stay consistent.
  useEffect(() => {
    return subscribeToAssignment(experimentKey, (next) => {
      if (next && variants.includes(next) && next !== variant) {
        setVariant(next);
      }
    });
  }, [experimentKey, variants, variant]);

  const track = useMemo(
    () =>
      (event: string, metadata?: Record<string, unknown>) => {
        const path = currentPagePath();
        const enriched = {
          page_path: path,
          service: serviceFromPath(path),
          ...(metadata ?? {}),
        };
        // Best-effort, non-blocking
        void (supabase.from("experiment_events") as any)
          .insert({
            experiment_key: experimentKey,
            variant,
            event,
            session_id: sessionId,
            metadata: enriched,
          })
          .then(({ error }: { error: { message: string } | null }) => {
            if (error && import.meta.env.DEV) {
              // eslint-disable-next-line no-console
              console.warn("[experiment] track failed", error.message);
            }
          });
      },
    [experimentKey, variant, sessionId],
  );

  useEffect(() => {
    if (!assigned || viewedRef.current) return;
    viewedRef.current = true;
    track("view");
  }, [assigned, track]);


  return { variant, track };
}
